import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'error' | 'info' | 'success' | 'warning';

const variantClasses: Record<Variant, string> = {
  error: 'border-red-200 bg-red-50 text-red-800',
  info: 'border-brand-200 bg-brand-50 text-brand-800',
  success: 'border-accent-200 bg-accent-50 text-accent-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
};

/**
 * Renders a server-provided message plus optional validation `details`
 * (the API error contract's `details: string[]`).
 */
export function Alert({
  className,
  variant = 'info',
  title,
  details,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  variant?: Variant;
  title?: ReactNode;
  details?: string[];
}): JSX.Element {
  return (
    <div
      role="alert"
      className={cn(
        'rounded-lg border px-4 py-3 text-sm shadow-sm',
        variantClasses[variant],
        className,
      )}
      {...props}
    >
      {title ? <p className="font-medium">{title}</p> : null}
      {children ? <div className={cn(title ? 'mt-1' : undefined)}>{children}</div> : null}
      {details && details.length > 0 ? (
        <ul className="mt-2 list-disc pl-5">
          {details.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
